/**
 * Align data hook — ONE load for the whole app.
 *
 * The strategy-alignment layer is bare async functions with no store and no
 * caching, so three screens each querying would mean three round trips of the
 * same data. This hook loads once at the app root and hands the result down.
 *
 * Compass findings are read here too: the detector already persists findings
 * to `agent_findings`, but until now nothing rendered them — Align is their
 * first surface, not a second detector.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  generateAlignmentReport,
  getObjectiveTree,
  type AlignmentReport,
  type ObjectiveNode,
} from '@/lib/strategyAlignment';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useAgentFindingsStore } from '@/stores/prioritization/agentFindingsStore';
import { COMPASS_AGENT_TYPE } from '@/lib/intelligence/compassAnalyzer';

export interface AlignmentData {
  loading: boolean;
  error: string | null;
  workspaceId: string | null;
  report: AlignmentReport | null;
  tree: ObjectiveNode[];
  /** Item titles for anything referenced by a mapping or gap. */
  titleFor: (itemId: string) => string;
  reload: () => void;
}

export function useAlignment(): AlignmentData {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const items = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const fetchFindings = useAgentFindingsStore((s) => s.fetchActive);

  const [report, setReport] = useState<AlignmentReport | null>(null);
  const [tree, setTree] = useState<ObjectiveNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    // No workspace chosen: there is nothing to read. Stop "loading" so the
    // screens can say so instead of showing "Reading…" forever.
    if (!workspaceId) {
      setReport(null);
      setTree([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        // Active items are the "priorities" the report scores alignment against.
        const active = items.filter((i) => !i.exit_status || i.exit_status === 'active');
        const priorityIds = active.map((i) => i.id);
        const names = new Map(active.map((i) => [i.id, i.title]));
        const [rep, objTree] = await Promise.all([
          generateAlignmentReport(workspaceId, priorityIds, names),
          getObjectiveTree(workspaceId),
        ]);
        if (cancelled) return;
        setReport(rep);
        setTree(objTree);
        // Surface whatever Compass already found; never run the detector here.
        void fetchFindings(workspaceId, COMPASS_AGENT_TYPE);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not read the alignment picture.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // `items.length` (not `items`) keeps this from re-firing on every store touch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, items.length, nonce, fetchFindings]);

  const titleById = useMemo(() => new Map(items.map((i) => [i.id, i.title])), [items]);
  const titleFor = useCallback(
    (itemId: string) => titleById.get(itemId) ?? 'Untitled item',
    [titleById],
  );

  return { loading, error, workspaceId, report, tree, titleFor, reload };
}

/** Active Compass findings, already persisted by the detector. */
export function useCompassFindings() {
  const byAgent = useAgentFindingsStore((s) => s.byAgent);
  const accept = useAgentFindingsStore((s) => s.accept);
  const dismiss = useAgentFindingsStore((s) => s.dismiss);
  const slice = byAgent?.[COMPASS_AGENT_TYPE];
  return {
    findings: slice?.active ?? [],
    loading: slice?.isLoading ?? false,
    accept,
    dismiss,
  };
}
